import { Box, Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConsentAnswer, ConsentSummary, getConsentAnswers, getConsentSummary } from "../../service/ConsentService";
import { outlinedButton, StyledTableCell, StyledTableRow, TableWrapper } from "../logs/logStyles";

// The participants' answers to the information about their data (accept / decline before playing).
// Totals for everyone; per person only in support mode (reason logged) - e.g. as documentation for the customer.
export default function ConsentAnswers({ breefId }: { breefId: string }) {
  const [t] = useTranslation("global");
  const [summary, setSummary] = useState<ConsentSummary | null>(null);
  const [answers, setAnswers] = useState<ConsentAnswer[] | null>(null);

  useEffect(() => {
    getConsentSummary(breefId).then(setSummary);
  }, [breefId]);

  const exportCsv = () => {
    const rows = [["Navn", "Telefon", "Svar", "Tekstversion", "Tidspunkt"], ...(answers || []).map((row) => [row.name, row.phoneNumber, row.answer, row.version, new Date(row.answeredAt).toISOString()])];
    const csv = rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `svar-${breefId}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ marginBottom: "2%" }}>
      <Box sx={{ width: "100%", mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="h6">{t("consent.title")}</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" sx={outlinedButton} onClick={() => getConsentAnswers(breefId).then(setAnswers)}>
            {t("consent.showPerPerson")}
          </Button>
          {answers && answers.length > 0 && (
            <Button variant="outlined" sx={outlinedButton} onClick={exportCsv}>
              {t("consent.export")}
            </Button>
          )}
        </Box>
      </Box>
      {summary && (
        <Typography variant="body2" sx={{ width: "100%", mt: 1 }}>
          {t("consent.summary", { ...summary })}
        </Typography>
      )}
      {answers && (
        <TableWrapper component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell>{t("consent.name")}</StyledTableCell>
                <StyledTableCell>{t("consent.phone")}</StyledTableCell>
                <StyledTableCell>{t("consent.answer")}</StyledTableCell>
                <StyledTableCell>{t("consent.version")}</StyledTableCell>
                <StyledTableCell>{t("consent.time")}</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {answers.map((row, index) => (
                <StyledTableRow key={`${row.participantId}-${index}`}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.phoneNumber}</TableCell>
                  <TableCell>{t(`consent.${row.answer}`)}</TableCell>
                  <TableCell>{row.version}</TableCell>
                  <TableCell>{new Date(row.answeredAt).toLocaleString("da-DK")}</TableCell>
                </StyledTableRow>
              ))}
              {!answers.length && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center" }}>
                    {t("consent.none")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableWrapper>
      )}
    </Box>
  );
}
